/*----------------------------------
- SALES PAGE: ASK YOUR AI
----------------------------------*/

/**
 * @docs docs/features/sales-page
 * @adr  ADR-0067
 * @rule The prompt carries the day's aggregates the page already shows (each window's count,
 *       median and trend, the busiest ending, the rollup time), formatted here and never a row, a
 *       name or a single sale: an assistant reads figures, as from the mirrors.
 */

import type { AskYourAIProps } from '@uniquedomains/components/ask-ai';
import { formatSalesCount, formatSalesRollup, formatSalesTrend, formatSalesUsd } from '@uniquedomains/components/contracts/sales/format';
import { resolveSalesFilterOptionLabel } from '@uniquedomains/components/contracts/sales/filters';
import type { PublicSalesPage } from '@uniquedomains/components/contracts/sales/public';
import type { GeoContext } from '@uniquedomains/components/geo/pageGeo';
import { fillSalesCopy } from '@uniquedomains/components/sales/salesCopy';

import { SALES_PAGE_CONTENT } from '@/client/catalogs/landing/sales/pageContent';

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const buildSalesAskAiFacts = (page: PublicSalesPage): string[] => {
    const words = SALES_PAGE_CONTENT.askAi;
    const busiest = page.pulse.busiestEnding7d;
    return [
        ...page.pulse.windows.map((window) => fillSalesCopy(words.window, {
            window: resolveSalesFilterOptionLabel('date_range', window.id),
            count: formatSalesCount(window.count),
            median: formatSalesUsd(window.medianUsd),
            trend: formatSalesTrend(window.trendPct),
        })),
        ...(busiest ? [fillSalesCopy(words.busiest, { ending: `.${busiest.ending}`, share: busiest.sharePct })] : []),
        fillSalesCopy(words.updated, { rollup: formatSalesRollup(page.freshness.rollupAt) }),
    ];
};

export const buildSalesAskAiContext = ({ page, canonicalUrl, geoContext }: { page: PublicSalesPage; canonicalUrl: string; geoContext: GeoContext }): AskYourAIProps => ({
    pageType: 'sales',
    pageUrl: canonicalUrl,
    canonicalPageUrl: canonicalUrl,
    pageTitle: SALES_PAGE_CONTENT.meta.pageTitle,
    facts: buildSalesAskAiFacts(page),
    geoContext,
});
